import React, { useState, useEffect } from "react"
import Modal from "aldoo-ra/Modal"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import Tabs from "aldoo-ra/Tabs"
import Checkbox from "aldoo-ra/Checkbox"
import Tooltip from "aldoo-ra/Tooltip"
import { InformationCircleIcon } from "@heroicons/react/24/outline"
import { showNotification } from "aldoo-ra/Notification"

export default function EnvironmentDialog({ environment, existingEnvironments, onSave, onClose, onDelete }) {
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    description: "",
    api: "",
    api_key: "",
    allowUpdates: true,
    askForConfirmation: true,
  })

  const [errors, setErrors] = useState({})
  const [activeTab, setActiveTab] = useState("details")

  useEffect(() => {
    if (environment) {
      setFormData({
        id: environment.id || "",
        name: environment.name || "",
        description: environment.description || "",
        api: environment.api || "",
        api_key: environment.api_key || "",
        allowUpdates: environment.allowUpdates !== undefined ? environment.allowUpdates : true,
        askForConfirmation: environment.askForConfirmation !== undefined ? environment.askForConfirmation : true,
      })
    }
  }, [environment])

  const generateId = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
  }

  const handleNameChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      name: value,
      // Auto-generate ID only for new environments
      id: environment ? prev.id : generateId(value),
    }))
    // Clear name error
    if (errors.name) {
      setErrors((prev) => ({ ...prev, name: null }))
    }
  }

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    // Clear field error
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }))
    }
  }

  const validate = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = "Name is required"
    }

    if (!formData.api.trim()) {
      newErrors.api = "API URL is required"
    } else {
      // Basic URL validation
      try {
        new URL(formData.api)
      } catch {
        newErrors.api = "Invalid URL format"
      }
    }

    if (!formData.api_key.trim()) {
      newErrors.api_key = "API Key is required"
    }

    // Check for duplicate ID (only for new or if ID changed)
    if (!environment || environment.id !== formData.id) {
      const isDuplicate = existingEnvironments.some((env) => env.id === formData.id)
      if (isDuplicate) {
        newErrors.id = "An environment with this ID already exists"
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleApply = () => {
    if (!validate()) {
      showNotification({
        message: "Please fix the errors before saving",
        className: "bg-admin_error text-white",
      })
      return
    }

    onSave(formData)
  }

  const tabs = [
    { label: "Details", value: "details" },
    { label: "Access", value: "access" },
    { label: "Security", value: "security" },
  ]

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      variant="admin_centered"
      title={environment ? "Edit Environment" : "Add Environment"}
      headerContent={
        (formData.id || generateId(formData.name)) && (
          <span className="px-2 py-1 bg-admin_primary/10 text-admin_primary text-xs font-mono rounded-full border border-admin_primary/20 mr-10">
            {formData.id || generateId(formData.name)}
          </span>
        )
      }
    >
      {/* Tabs */}
      <div className="px-6 pt-4">
        <Tabs
          tabs={tabs}
          value={activeTab}
          onChange={setActiveTab}
          variant="admin"
        />
      </div>

      {/* Form */}
      <div className="p-6 space-y-6">
        {/* Details Tab */}
        {activeTab === "details" && (
          <>
            {/* Name */}
            <div>
              <Typography variant="admin_label">
                Name
              </Typography>

              <Input
                variant="admin"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                onClear={() => handleNameChange("")}
                placeholder="e.g., Production Master"
                showClearButton={true}
                className="w-full"
              />

              {errors.name && (
                <Typography 
                  variant="admin_txt1" 
                  className="block text-admin_error mt-1"
                >
                  {errors.name}
                </Typography>
              )}
            </div>

            {/* Description */}
            <div>
              <Typography variant="admin_label">
                Description
              </Typography>

              <Input
                variant="admin"
                value={formData.description}
                onChange={(e) => handleFieldChange("description", e.target.value)}
                onClear={() => handleFieldChange("description", "")}
                placeholder="Enter description (optional)"
                showClearButton={true}
                className="w-full"
              />
            </div>

            {/* ID Error (if any) */}
            {errors.id && (
              <div className="bg-admin_error/10 border border-admin_error/20 rounded p-3">
                <Typography 
                  variant="admin_txt1" 
                  className="text-admin_error"
                >
                  {errors.id}
                </Typography>
              </div>
            )}
          </>
        )}

        {/* Access Tab */}
        {activeTab === "access" && (
          <>
            {/* API URL */}
            <div>
              <Typography variant="admin_label">
                API URL
              </Typography>

              <Input
                variant="admin"
                value={formData.api}
                onChange={(e) => handleFieldChange("api", e.target.value)}
                onClear={() => handleFieldChange("api", "")}
                placeholder="e.g., https://api.example.com"
                showClearButton={true}
                className="w-full"
              />

              {errors.api && (
                <Typography 
                  variant="admin_txt1" 
                  className="text-admin_error"
                >
                  {errors.api}
                </Typography>
              )}
            </div>

            {/* API Key */}
            <div>
              <div className="flex items-center gap-2">
                <Typography variant="admin_label">
                  API Key
                </Typography>

                {formData.api_key && (
                  <Tooltip 
                    text="Copy API Key" 
                    variant="admin" 
                    position="top"
                  >
                    < Button
                      variant="admin_icon"
                      onClick={() => {
                        navigator.clipboard.writeText(formData.api_key)
                        showNotification({
                          message: "API Key copied to clipboard",
                          className: "bg-admin_success text-white",
                        })
                      }}
                      className="text-admin_text_secondary"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </Button>
                  </Tooltip>
                )}
              </div>

              <Input
                variant="admin"
                value={formData.api_key}
                onChange={(e) => handleFieldChange("api_key", e.target.value)}
                onClear={() => handleFieldChange("api_key", "")}
                placeholder="Enter API key"
                showClearButton={true}
                className="w-full"
              />

              {errors.api_key && (
                <Typography 
                  variant="admin_txt1" 
                  className="text-admin_error"
                >
                  {errors.api_key}
                </Typography>
              )}
            </div>
          </>
        )}

        {/* Security Tab */}
        {activeTab === "security" && (
          <>
            {/* Allow Updates */}
            <div className="flex items-center gap-2">
              <Checkbox
                variant="admin"
                label="Allow Updates"
                isOn={formData.allowUpdates}
                onChange={(value) => handleFieldChange("allowUpdates", value)}
              />

              <Tooltip
                text="Enable or disable update deployments to this environment"
                variant="admin"
                position="right"
              >
                <InformationCircleIcon className="w-4 h-4 text-admin_text_secondary cursor-help" />
              </Tooltip>
            </div>

            {/* Divider */}
            <div className="border-t border-admin_bordered" />

            {/* Ask for Confirmation */}
            <div className="flex items-center gap-2">
              <Checkbox
                variant="admin"
                label="Ask for Confirmation"
                isOn={formData.askForConfirmation}
                onChange={(value) => handleFieldChange("askForConfirmation", value)}
              />

              <Tooltip
                text="Request confirmation before uploading updates to this environment"
                variant="admin"
                position="right"
              >
                <InformationCircleIcon className="w-4 h-4 text-admin_text_secondary cursor-help" />
              </Tooltip>
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between p-6 border-t border-admin_bordered">
        {environment ? (
          <Button
            variant="admin_error"
            onClick={() => onDelete(environment)}
            confirm={{
              title: "Delete Environment",
              message: `Are you sure you want to delete "${environment.name}"?`,
              confirmText: "Delete",
              cancelText: "Cancel",
            }}
          >
            Delete
          </Button>
        ) : (
          <div />
        )}
        <Button 
          variant="admin_primary" 
          onClick={handleApply} 
          className="min-w-[200px]"
        >
          Apply
        </Button>
      </div>
    </Modal>
  )
}
